import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSortAlphaDown, FaSortAlphaUp } from 'react-icons/fa';
import { NS } from '../../U3130';
import type { U3130HonorariumSortOrder } from '../../tools/u3130HonorariumGridSort';
import { U3130_HONORARIUM_HEADER_TH_CLASS } from '../../tools/u3130HonorariumGridCompactStyles';

export interface U3130HonorariumSortableMetaHeaderProps {
  labelKey: string;
  labelDefault: string;
  active: boolean;
  order: U3130HonorariumSortOrder;
  onSort: () => void;
}

const U3130HonorariumSortableMetaHeader: React.FC<U3130HonorariumSortableMetaHeaderProps> = ({
  labelKey,
  labelDefault,
  active,
  order,
  onSort,
}) => {
  const { t } = useTranslation();
  const label = t(labelKey, { ns: NS, defaultValue: labelDefault });
  const ariaSort = active ? (order === 'ASC' ? 'ascending' : 'descending') : 'none';
  const sortHint = active
    ? order === 'ASC'
      ? t('ariaSortColumnDescending', { ns: NS, column: label, defaultValue: `Trier par ${label}, ordre décroissant` })
      : t('ariaSortColumnAscending', { ns: NS, column: label, defaultValue: `Trier par ${label}, ordre croissant` })
    : t('ariaSortColumnActivate', { ns: NS, column: label, defaultValue: `Trier par ${label}` });

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSort();
      }
    },
    [onSort],
  );

  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      title={sortHint}
      tabIndex={0}
      onClick={onSort}
      onKeyDown={handleKeyDown}
      className={`${U3130_HONORARIUM_HEADER_TH_CLASS} cursor-pointer select-none hover:bg-gray-200/50 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500`}
    >
      <span className="inline-flex max-w-full items-center gap-1">
        <span className="truncate">{label}</span>
        {active && order === 'ASC' ? <FaSortAlphaDown className="h-3.5 w-3.5 shrink-0" aria-hidden /> : null}
        {active && order === 'DESC' ? <FaSortAlphaUp className="h-3.5 w-3.5 shrink-0" aria-hidden /> : null}
      </span>
    </th>
  );
};

export default U3130HonorariumSortableMetaHeader;
